/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module HealthMonitorModule
 */

import {
    Component,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    SimpleChanges,
    TemplateRef,
    ViewChild,
} from '@angular/core';

import {
    HealthMonitor,
    HealthMonitorHttp,
    HealthMonitorSSLAttributes,
} from 'object-types';

import {
    HealthMonitorAuthType,
    HealthMonitorType,
    HTTPMethod,
} from 'generated-types';

import { L10nService } from '@vmw/ngx-vip';

import {
    HealthMonitorHttpConfigItem,
    HealthMonitorItem,
} from 'ajs/modules/healthmonitor/factories';

import {
    CertificateCollection,
    PKIProfileCollection,
    SSLProfileCollection,
    TPkiProfileCollection,
} from 'ajs/modules/security';

import { SchemaService } from 'ajs/modules/core/services/schema-service/schema.service';

import { IAviDataGridConfig } from 'ng/modules/data-grid/components/avi-data-grid/avi-data-grid.types';

import * as globalL10n from 'global-l10n';
import * as l10n from './http-health-monitor-config.l10n';

interface IHttpHealthMonitorGridRow {
    header: string,
    value: string,
}

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * SSLProfileCollection Type.
 */
type TSslProfileCollection = typeof SSLProfileCollection;

/**
 * CertificateCollection Type.
 */
type TCertificateCollection = typeof CertificateCollection;

/**
 * @description Health Monitor Config Component for HTTP and HTTPS Health Monitor Types.
 *
 * @author Rajawant Prajapati, Zhiqian Liu, Umesh Chovatiya, Manideep Kotha
 */
@Component({
    selector: 'http-health-monitor-config',
    templateUrl: './http-health-monitor-config.component.html',
})
export class HttpHealthMonitorConfigComponent implements OnInit, OnDestroy {
    /**
     * Health Monitor ObjectTypeItem instance.
     */
    @Input()
    public editable: HealthMonitorItem;

    @Input()
    public isFederated = false;

    /**
     * Template ref for header input for HTTP Header Grid.
     */
    @ViewChild('gridHeaderTemplateRef')
    public gridHeaderTemplateRef: TemplateRef<HTMLElement>;

    /**
     * Template ref for value input for HTTP Header Grid.
     */
    @ViewChild('gridValueTemplateRef')
    public gridValueTemplateRef: TemplateRef<HTMLElement>;

    /**
     * Health Monitor config type. This can be either http_monitor or https_monitor.
     */
    public httpConfigItem: HealthMonitorHttpConfigItem;

    /**
     * Flag to indicate if ssl_attributes object is present in this.httpConfigItem.
     */
    public sslAttributesEnabled = false;

    /**
     * Flag to show fields applicable only for HTTPS type.
     */
    public isHttpsType = false;

    /**
     * Flag to show fields applicable only for HTTP2S type.
     */
    public isHttp2sType = false;

    /**
     * Flag to show fields applicable only for HTTP2 or HTTP2S type.
     */
    public isHttp2Types = false;

    /**
     * Hidden HTTP Methods for HTTP Headers grid.
     */
    public readonly hiddenHttpMethodEnums: HTTPMethod[] = [];

    /**
     * Object types used in template.
     */
    public readonly objectTypes = {
        HealthMonitor,
        HealthMonitorHttp,
        HealthMonitorSSLAttributes,
    };

    /**
     * SSLProfileCollection instance.
     */
    public readonly sslProfileCollection: SSLProfileCollection;

    /**
     * CertificateCollection instance.
     */
    public readonly certificateCollection: CertificateCollection;

    /**
     * PKIProfileCollection instance.
     */
    public readonly pkiProfileCollection: PKIProfileCollection;

    /**
     * Get keys from source bundles for template usage.
     */
    public readonly l10nKeys = l10nKeys;

    /**
     * Get global keys from source bundles for template usage.
     */
    public readonly globalL10nKeys = globalL10nKeys;

    public httpHeadersGridConfig: IAviDataGridConfig;

    public httpHeaderGridRows: IHttpHealthMonitorGridRow[] = [];

    public readonly healthMonitorTypesHash = {
        HEALTH_MONITOR_HTTP: globalL10nKeys.httpLabel,
        HEALTH_MONITOR_HTTPS: globalL10nKeys.httpsLabel,
        HEALTH_MONITOR_HTTP2: l10nKeys.http2Label,
        HEALTH_MONITOR_HTTP2S: l10nKeys.http2sLabel,
    };

    /**
     * HTTP Methods for HTTP Headers grid.
     */
    private readonly httpMethodEnumValueHash = [
        HTTPMethod.HTTP_METHOD_GET,
        HTTPMethod.HTTP_METHOD_HEAD,
        HTTPMethod.HTTP_METHOD_POST,
    ];

    constructor(
        private readonly l10nService: L10nService,
        @Inject(SSLProfileCollection)
        SSLProfileCollection: TSslProfileCollection,
        @Inject(CertificateCollection)
        CertificateCollection: TCertificateCollection,
        @Inject(PKIProfileCollection)
        PKIProfileCollection: TPkiProfileCollection,
        @Inject(SchemaService)
        private readonly schemaService: SchemaService,
    ) {
        l10nService.registerSourceBundles(dictionary);

        this.sslProfileCollection = new SSLProfileCollection();
        this.certificateCollection = new CertificateCollection();
        this.pkiProfileCollection = new PKIProfileCollection();
    }

    /**
     * Used to disable Use Exact Request checkbox if returns true.
     */
    public get disableUseExactRequestCheckbox(): boolean {
        const { config } = this.httpConfigItem;
        const { auth_type: authType } = config;

        return authType === HealthMonitorAuthType.AUTH_NTLM;
    }

    /** @override */
    public ngOnInit(): void {
        const { editable } = this;

        this.isHttpsType = editable.isHttpsType();
        this.isHttp2sType = editable.getType() === HealthMonitorType.HEALTH_MONITOR_HTTP2S;
        this.isHttp2Types = editable.isHttp2Types();

        this.httpConfigItem = editable.healthMonitorTypeConfig;
        this.sslAttributesEnabled = Boolean(this.httpConfigItem.config.ssl_attributes);

        if (editable.isHttp2Types) {
            this.httpHeaderGridRows = this.httpConfigItem.config.http_headers?.map((row: string) => {
                const [header, value] = row.split(': ');

                return {
                    header,
                    value,
                };
            }) ?? [];

            // Hidden HTTP Methods for HTTP Headers are filtered.
            this.schemaService.getEnumValues('HTTPMethod')
                .forEach(item => {
                    if (!this.httpMethodEnumValueHash.includes(item.value as HTTPMethod)) {
                        this.hiddenHttpMethodEnums.push(item.value as HTTPMethod);
                    }
                });
        }
    }

    /** @override */
    public ngAfterViewInit(): void {
        const { l10nService } = this;

        this.httpHeadersGridConfig = {
            fields: [{
                id: 'health-monitor-headers',
                label: l10nService.getMessage(l10nKeys.headerLabel),
                templateRef: this.gridHeaderTemplateRef,
            }, {
                id: 'health-monitor-values',
                label: l10nService.getMessage(globalL10nKeys.valueLabel),
                templateRef: this.gridValueTemplateRef,
            },
            ],
            multipleactions: [{
                label: l10nService.getMessage(globalL10nKeys.removeLabel),
                onClick: (httpHeaderGridRows: IHttpHealthMonitorGridRow[]) => {
                    httpHeaderGridRows.forEach((gridRow: IHttpHealthMonitorGridRow) => {
                        this.deleteRow(gridRow);
                    });
                },
            }],
            singleactions: [{
                label: l10nService.getMessage(globalL10nKeys.removeLabel),
                onClick: (gridRow: IHttpHealthMonitorGridRow) => this.deleteRow(gridRow),
            }],
        };
    }

    /** @override */
    public ngOnChanges(changes: SimpleChanges): void {
        const { isFederated } = changes;
        const params = {
            is_federated: isFederated?.currentValue,
        };

        this.pkiProfileCollection.setParams(params);
        this.sslProfileCollection.setParams(params);
        this.certificateCollection.setParams(params);
    }

    /**
     * Handle change in enable-ssl-attributes checkbox.
     * If the checkbox is checked, add the ssl_attributes object to the config.
     * If the checkbox is unchecked, remove the ssl_attributes object from the config.
     */
    public handleEnableSslAttributesChange(enableSslAttributes: boolean): void {
        this.sslAttributesEnabled = enableSslAttributes;

        if (enableSslAttributes) {
            this.httpConfigItem.addSslAttributes();
        } else {
            this.httpConfigItem.config.ssl_attributes = undefined;
        }
    }

    /** @override */
    public ngOnDestroy(): void {
        this.sslProfileCollection.destroy();
        this.certificateCollection.destroy();
        this.pkiProfileCollection.destroy();
    }

    /**
     * Handle change in use-pool-sni-server-name checkbox.
     * If the checkbox is checked, remove server_name field.
     */
    public handleUsePoolSNIServerNameChange(usePoolSNIServerName: boolean): void {
        if (usePoolSNIServerName) {
            delete this.httpConfigItem.config.ssl_attributes?.config.server_name;
        }
    }

    /**
     * Handler for row change operation for http headers grid.
     */
    public handleRowChange(gridRow: IHttpHealthMonitorGridRow, index: number): void {
        this.httpConfigItem.config.http_headers[index] = `${gridRow.header}: ${gridRow.value}`;
    }

    /**
     * Handler for row add operation for http headers grid.
     */
    public addRow(): void {
        this.httpHeaderGridRows.push({
            header: '',
            value: '',
        });

        this.updateHttpHeaderGrid();
    }

    /**
     * Handler for row delete operation for http headers grid.
     */
    private deleteRow(gridRow: IHttpHealthMonitorGridRow): void {
        const index = this.httpHeaderGridRows.indexOf(gridRow);

        this.httpHeaderGridRows.splice(index, 1);
        this.updateHttpHeaderGrid();
    }

    private updateHttpHeaderGrid(): void {
        this.httpConfigItem.config.http_headers =
            this.httpHeaderGridRows.map((gridRow: IHttpHealthMonitorGridRow) => {
                return `${gridRow.header}: ${gridRow.value}`;
            });
    }
}
